import React from 'react';
import { Building2, Phone, Mail, Globe, FileText } from 'lucide-react';

export const PrintStock = ({ inventory, warehouse }) => {
  const handlePrint = () => {
    window.print();
  };

  const currentDate = new Date().toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'long',
    year: 'numeric'
  });

  const totalStock = inventory.reduce((sum, item) => sum + item.current_stock, 0);
  const lowStockCount = inventory.filter(item => item.current_stock > 0 && item.current_stock < 500).length;
  const outOfStockCount = inventory.filter(item => item.current_stock === 0).length;

  return (
    <div className="print-container">
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          .print-container, .print-container * {
            visibility: visible;
          }
          .print-container {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            padding: 0;
            margin: 0;
          }
          .no-print {
            display: none !important;
          }
          .print-content {
            padding: 20px;
          }
          @page {
            size: A4;
            margin: 10mm;
          }
          table {
            page-break-inside: auto;
          }
          tr {
            page-break-inside: avoid;
            page-break-after: auto;
          }
        }
        @media screen {
          .print-content {
            max-width: 900px;
            margin: 0 auto;
          }
        }
      `}</style>

      <button 
        onClick={handlePrint}
        className="no-print mb-4 px-6 py-3 bg-lime-700 text-white rounded-md hover:bg-lime-800 flex items-center gap-2 font-medium"
      >
        <FileText className="w-5 h-5" />
        Print Stock Report
      </button>

      <div className="print-content bg-white p-8 border border-stone-200 rounded-lg" style={{ fontFamily: 'Arial, sans-serif' }}>
        {/* Letterhead Header */}
        <div className="border-b-4 border-double border-lime-700 pb-4 mb-6">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-3xl font-bold" style={{ color: '#4d7c0f', fontFamily: 'Georgia, serif', letterSpacing: '1px' }}>
                S.P SINGH AND SONS
              </h1>
              <p className="text-sm text-stone-600 mt-2 leading-relaxed">
                <strong>Regd Office:</strong> 1158, Kanak Mandi<br />
                Near Golden Temple, Amritsar<br />
                (Punjab) India - 143001
              </p>
            </div>
            <div className="text-right">
              <img src="/logo.jpeg" alt="SPS Groups" className="h-20 mb-2 ml-auto" style={{ objectFit: 'contain' }} />
              <p className="text-lg font-bold" style={{ color: '#4d7c0f' }}>SPS GROUPS</p>
            </div>
          </div>
          
          {/* Contact Row */}
          <div className="flex justify-between mt-4 pt-3 border-t border-stone-200 text-xs text-stone-600">
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1">
                <Phone className="w-3 h-3" />
                +91 9815269088, +91 9463564199
              </span>
              <span className="flex items-center gap-1">
                <Mail className="w-3 h-3" />
                spsinghandsons@gmail.com
              </span>
            </div>
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1">
                <Globe className="w-3 h-3" />
                www.amanbrand.in
              </span>
              <span><strong>GSTIN:</strong> 03AOIPS7062J1ZN</span>
            </div>
          </div>
        </div>

        {/* Report Title */}
        <div className="text-center mb-6 py-3 bg-stone-50 rounded-lg border border-stone-200">
          <h2 className="text-2xl font-bold text-stone-800 mb-2 tracking-wide">STOCK INVENTORY REPORT</h2>
          <div className="flex justify-center items-center gap-6 text-sm text-stone-600">
            <div className="flex items-center gap-2">
              <Building2 className="w-4 h-4 text-lime-700" />
              <span><strong>Warehouse:</strong> {warehouse}</span>
            </div>
            <span className="text-stone-300">|</span>
            <span><strong>Report Date:</strong> {currentDate}</span>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-4 gap-4 mb-6 no-print">
          <div className="bg-lime-50 border border-lime-200 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-lime-700">{inventory.length}</p>
            <p className="text-xs text-stone-600">Total Products</p>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-blue-700">{totalStock.toFixed(0)}</p>
            <p className="text-xs text-stone-600">Total Stock (kg)</p>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-amber-700">{lowStockCount}</p>
            <p className="text-xs text-stone-600">Low Stock Items</p>
          </div>
          <div className="bg-rose-50 border border-rose-200 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-rose-700">{outOfStockCount}</p>
            <p className="text-xs text-stone-600">Out of Stock</p>
          </div>
        </div>

        {/* Stock Table */}
        <table className="w-full border-collapse" style={{ fontSize: '11px' }}>
          <thead>
            <tr style={{ backgroundColor: '#4d7c0f', color: 'white' }}>
              <th className="text-left p-2 border border-stone-400" style={{ width: '40px' }}>S.No</th>
              <th className="text-left p-2 border border-stone-400">Product Name</th>
              <th className="text-left p-2 border border-stone-400">Category</th>
              <th className="text-left p-2 border border-stone-400">Vendor</th>
              <th className="text-right p-2 border border-stone-400">In Stock (kg)</th>
              <th className="text-right p-2 border border-stone-400">Out Stock (kg)</th>
              <th className="text-right p-2 border border-stone-400">Current Stock (kg)</th>
              <th className="text-center p-2 border border-stone-400">Status</th>
            </tr>
          </thead>
          <tbody>
            {inventory.length === 0 ? (
              <tr>
                <td colSpan={8} className="text-center p-6 text-stone-500 bg-stone-50">
                  No inventory data available for this warehouse
                </td>
              </tr>
            ) : (
              inventory.map((item, index) => {
                let status = "Healthy";
                let statusStyle = { backgroundColor: '#dcfce7', color: '#166534' };
                
                if (item.current_stock === 0) {
                  status = "Out of Stock";
                  statusStyle = { backgroundColor: '#fee2e2', color: '#991b1b' };
                } else if (item.current_stock < 500) {
                  status = "Low Stock";
                  statusStyle = { backgroundColor: '#fef3c7', color: '#92400e' };
                }

                return (
                  <tr key={item.id} style={{ backgroundColor: index % 2 === 0 ? '#ffffff' : '#fafaf9' }}>
                    <td className="p-2 border border-stone-300 text-center">{index + 1}</td>
                    <td className="p-2 border border-stone-300 font-medium">{item.product_name}</td>
                    <td className="p-2 border border-stone-300">{item.category}</td>
                    <td className="p-2 border border-stone-300">{item.vendor}</td>
                    <td className="p-2 border border-stone-300 text-right">{item.in_stock.toFixed(2)}</td>
                    <td className="p-2 border border-stone-300 text-right">{item.out_stock.toFixed(2)}</td>
                    <td className="p-2 border border-stone-300 text-right font-bold">{item.current_stock.toFixed(2)}</td>
                    <td className="p-2 border border-stone-300 text-center">
                      <span style={{
                        padding: '3px 8px',
                        borderRadius: '4px',
                        fontSize: '10px',
                        fontWeight: 'bold',
                        ...statusStyle
                      }}>
                        {status}
                      </span>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
          <tfoot>
            <tr style={{ backgroundColor: '#4d7c0f', color: 'white', fontWeight: 'bold' }}>
              <td colSpan={6} className="p-2 border border-stone-400 text-right">
                TOTAL CURRENT STOCK:
              </td>
              <td className="p-2 border border-stone-400 text-right text-lg">
                {totalStock.toFixed(2)} kg
              </td>
              <td className="p-2 border border-stone-400"></td>
            </tr>
          </tfoot>
        </table>

        {/* Stock Summary */}
        <div className="mt-6 p-4 bg-stone-50 rounded-lg border border-stone-200">
          <h4 className="font-bold text-stone-800 mb-2">Stock Summary</h4>
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <span className="text-stone-600">Total Products:</span>
              <span className="font-bold ml-2">{inventory.length}</span>
            </div>
            <div>
              <span className="text-stone-600">Total Stock:</span>
              <span className="font-bold ml-2">{totalStock.toFixed(2)} kg</span>
            </div>
            <div>
              <span className="text-stone-600">Low Stock Items:</span>
              <span className="font-bold ml-2 text-amber-700">{lowStockCount}</span>
            </div>
          </div>
        </div>

        {/* Signature Section */}
        <div className="mt-10 pt-6 border-t-2 border-stone-300">
          <div className="flex justify-between text-sm text-stone-600">
            <div className="w-1/3">
              <p className="mb-8"><strong>Prepared By:</strong></p>
              <div className="border-t border-stone-400 pt-2">
                <p>Name: ___________________</p>
                <p className="mt-2">Date: ___________________</p>
              </div>
            </div>
            <div className="w-1/3 text-center">
              <p className="mb-8"><strong>Checked By:</strong></p>
              <div className="border-t border-stone-400 pt-2">
                <p>Name: ___________________</p>
                <p className="mt-2">Date: ___________________</p>
              </div>
            </div>
            <div className="w-1/3 text-right">
              <p className="mb-8"><strong>Authorized By:</strong></p>
              <div className="border-t border-stone-400 pt-2">
                <p>Name: ___________________</p>
                <p className="mt-2">Date: ___________________</p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 pt-4 border-t border-stone-200 text-center">
          <p className="text-xs text-stone-500">
            This is a computer-generated report from SPS Groups Inventory Management System.
          </p>
          <p className="text-xs text-stone-400 mt-1">
            Generated on {new Date().toLocaleString('en-IN')} • © {new Date().getFullYear()} S.P Singh and Sons
          </p>
        </div>
      </div>
    </div>
  );
};

export default PrintStock;
